import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FolderOpen, ArrowRight, MapPin, Pencil, Search } from "lucide-react";
import { api, fmtINR, fmtDate, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import DocumentsDialog from "./DocumentsDialog";

const n = (v) => Number(v || 0);
const qty = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export function StockByLocation() {
  const [data, setData] = useState(null);
  const [loc, setLoc] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/stock-by-location", { params: { location: loc === "all" ? "" : loc } }); setData(d); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [loc]);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => !q || [r.product, r.grade, r.batch, r.location].some((x) => String(x || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-3" data-testid="stock-by-location">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="loc-search" placeholder="Search product / batch / location..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 bg-white h-9 w-72" /></div>
        <Select value={loc} onValueChange={setLoc}>
          <SelectTrigger data-testid="loc-filter" className="w-52 bg-white h-9"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Locations</SelectItem>{(data?.locations || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-slate-600 ml-auto">Stock Value at selected location <b className="font-mono" data-testid="loc-total-value">{fmtINR(data?.total_stock_value)}</b></span>
      </div>

      {!!(data?.by_location || []).length && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="loc-cards">
          {data.by_location.map((b) => (
            <div key={b.location} data-testid={`loc-card-${b.location}`} className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.location}</div>
              <div className="text-base font-mono font-semibold tabular-nums mt-1">{qty(b.quantity)}</div>
              <div className="text-xs text-slate-400">{fmtINR(b.stock_value)}</div>
            </div>))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {["Product", "Grade", "Batch", "Location", "Available Quantity", "Unit", "Unit Cost", "Stock Value"].map((h) => (
              <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>
            {!rows.length ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="loc-empty">No stock at this location</td></tr>
              : rows.map((r) => (
                <tr key={`${r.product_id}-${r.location}-${r.batch}`} data-testid={`loc-row-${r.product_id}-${r.location}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{r.product}</td>
                  <td className="px-3 py-2 text-slate-600">{r.grade}</td>
                  <td className="px-3 py-2 text-slate-600">{r.batch}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="rounded-full px-2">{r.location}</Badge></td>
                  <td className="px-3 py-2 font-mono tabular-nums">{qty(r.quantity)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.unit}</td>
                  <td className="px-3 py-2 font-mono tabular-nums">{fmtINR(r.unit_cost)}</td>
                  <td className="px-3 py-2 font-mono tabular-nums font-semibold">{fmtINR(r.stock_value)}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StockTransfers() {
  const { user } = useAuth();
  const can = (a) => hasPerm(user, "inventory", a);
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState(null);
  const [locOpen, setLocOpen] = useState(false);
  const [locForm, setLocForm] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [t, l, p] = await Promise.all([api.get("/stock-transfers"), api.get("/stock-locations"), api.get("/products")]);
      setRows(t.data); setLocations(l.data); setProducts(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const active = useMemo(() => locations.filter((l) => (l.status || "Active") === "Active"), [locations]);

  const openCreate = () => {
    setForm({ movement_date: new Date().toISOString().slice(0, 10), from_location: active[0]?.name || "", to_location: "", quantity: "" });
    setOpen(true);
  };
  const pickProduct = (pid) => {
    const p = products.find((x) => x.id === pid) || {};
    setForm((f) => ({ ...f, product_id: pid, product_name: p.product_name, unit: p.unit, grade: p.grade || "" }));
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/stock-transfers", {
        from_location: form.from_location, to_location: form.to_location, product_id: form.product_id,
        grade: form.grade || "", batch_number: form.batch_number || "", quantity: n(form.quantity),
        unit: form.unit || "", movement_date: form.movement_date, reference: form.reference || "", remarks: form.remarks || "",
      });
      toast.success("Stock transferred — total company stock is unchanged");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const remove = async (id) => {
    if (!window.confirm("Reverse this stock transfer? The stock goes back to the original location.")) return;
    try { await api.delete(`/stock-transfers/${id}`); toast.success("Transfer reversed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveLoc = async () => {
    try {
      if (locForm.id) await api.put(`/stock-locations/${locForm.id}`, locForm);
      else await api.post("/stock-locations", locForm);
      toast.success("Location saved"); setLocForm({}); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delLoc = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try { await api.delete(`/stock-locations/${id}`); toast.success("Location deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filtered = rows.filter((r) => !q || [r.transfer_no, r.product_name, r.batch_number, r.from_location, r.to_location, r.reference].some((x) => String(x || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-3" data-testid="stock-transfers">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="transfer-search" placeholder="Search transfer / product / location..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 bg-white h-9 w-72" /></div>
        <div className="ml-auto flex gap-2">
          <Button data-testid="manage-locations-btn" variant="outline" className="rounded-sm gap-1.5" onClick={() => setLocOpen(true)}><MapPin className="w-4 h-4" /> Locations</Button>
          {can("create") && <Button data-testid="add-transfer-btn" onClick={openCreate} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><Plus className="w-4 h-4" /> New Transfer</Button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            {["Date", "Transfer No", "Product", "Grade", "Batch", "From → To", "Quantity", "Reference", "Created By", ""].map((h) => (
              <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>
            {!filtered.length ? <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400" data-testid="transfers-empty">No stock transfers recorded</td></tr>
              : filtered.map((r) => (
                <tr key={r.id} data-testid={`transfer-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(r.movement_date)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{r.transfer_no}</td>
                  <td className="px-3 py-2 text-slate-700">{r.product_name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.grade || "-"}</td>
                  <td className="px-3 py-2 text-slate-600">{r.batch_number || "-"}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">{r.from_location} <ArrowRight className="w-3 h-3 inline text-slate-400" /> {r.to_location}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono tabular-nums">{qty(r.quantity)} {r.unit}</td>
                  <td className="px-3 py-2 text-slate-500 max-w-[160px] truncate">{r.reference || r.remarks || "-"}</td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{r.created_by_name || "-"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button data-testid={`transfer-docs-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-500" title="Documents" onClick={() => setDocs({ type: "stock_transfer", id: r.id, title: r.transfer_no })}><FolderOpen className="w-4 h-4" /></Button>
                    {can("delete") && <Button data-testid={`transfer-delete-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>}
                  </td>
                </tr>))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-head">New Stock Transfer</DialogTitle>
            <DialogDescription>Quantity is deducted from the FROM location and added to the TO location — total company stock never changes.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><span className="text-xs text-slate-500">From Location *</span>
              <Select value={form.from_location || ""} onValueChange={(v) => setForm({ ...form, from_location: v })}>
                <SelectTrigger data-testid="transfer-from" className="h-9"><SelectValue placeholder="From" /></SelectTrigger>
                <SelectContent>{active.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select></div>
            <div><span className="text-xs text-slate-500">To Location *</span>
              <Select value={form.to_location || ""} onValueChange={(v) => setForm({ ...form, to_location: v })}>
                <SelectTrigger data-testid="transfer-to" className="h-9"><SelectValue placeholder="To" /></SelectTrigger>
                <SelectContent>{active.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select></div>
            <div><span className="text-xs text-slate-500">Movement Date</span><Input data-testid="transfer-date" type="date" value={form.movement_date || ""} onChange={(e) => setForm({ ...form, movement_date: e.target.value })} className="h-9" /></div>
            <div className="sm:col-span-2"><span className="text-xs text-slate-500">Product *</span>
              <Select value={form.product_id || ""} onValueChange={pickProduct}>
                <SelectTrigger data-testid="transfer-product" className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent></Select></div>
            <div><span className="text-xs text-slate-500">Grade</span><Input data-testid="transfer-grade" value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Batch</span><Input data-testid="transfer-batch" value={form.batch_number || ""} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Quantity *</span><Input data-testid="transfer-qty" type="number" step="0.001" value={form.quantity ?? ""} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="h-9" /></div>
            <div><span className="text-xs text-slate-500">Unit</span><Input data-testid="transfer-unit" value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="h-9" /></div>
            <div className="sm:col-span-3"><span className="text-xs text-slate-500">Reference / Remarks</span><Input data-testid="transfer-reference" value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="h-9" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="transfer-save" disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>{saving ? "Transferring..." : "Transfer Stock"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={locOpen} onOpenChange={() => { setLocOpen(false); setLocForm({}); }}>
        <DialogContent className="max-w-xl" data-testid="locations-dialog">
          <DialogHeader><DialogTitle className="font-head">Stock Locations</DialogTitle>
            <DialogDescription>Factories, warehouses and other places where stock can sit.</DialogDescription></DialogHeader>
          {can("create") && (
            <div className="flex flex-wrap items-end gap-2 border border-slate-200 rounded-sm p-3 bg-slate-50/60">
              <div className="flex-1 min-w-[180px]"><span className="text-xs text-slate-500">Location Name</span>
                <Input data-testid="location-name" value={locForm.name || ""} onChange={(e) => setLocForm({ ...locForm, name: e.target.value })} className="h-9 bg-white" /></div>
              <div className="flex-1 min-w-[160px]"><span className="text-xs text-slate-500">Description</span>
                <Input data-testid="location-desc" value={locForm.description || ""} onChange={(e) => setLocForm({ ...locForm, description: e.target.value })} className="h-9 bg-white" /></div>
              <Button data-testid="location-save" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm h-9" onClick={saveLoc}>{locForm.id ? "Update" : "Add"}</Button>
            </div>
          )}
          <div className="space-y-1 max-h-[45vh] overflow-y-auto">
            {locations.map((l) => (
              <div key={l.id} data-testid={`location-${l.id}`} className="flex items-center justify-between border-b border-slate-100 py-2">
                <div><div className="text-sm font-medium text-slate-800">{l.name}{l.is_default ? <span className="ml-2 text-[10px] text-slate-400">default</span> : null}</div>
                  <div className="text-xs text-slate-500">{l.description || ""}</div></div>
                <div>
                  {can("edit") && <Button data-testid={`location-edit-${l.id}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => setLocForm(l)}><Pencil className="w-4 h-4" /></Button>}
                  {can("delete") && !l.is_default && <Button data-testid={`location-delete-${l.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => delLoc(l.id)}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>))}
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => { setLocOpen(false); setLocForm({}); }}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {docs && <DocumentsDialog recordType={docs.type} recordId={docs.id} title={docs.title} onClose={() => setDocs(null)} />}
    </div>
  );
}
